import { faCopyright, faUser } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { AuthenticatedUserContext, CrfFormByCodesRoute, FormMapping, IMenuItem, InstitutionsByCodesRoute, OnlinePatientManagementContentProvider, OnlinePatientManagementContext, PatientDetailsByStudyNumberRoute, RouteLoading, useIsMobile } from "@ngt/opms";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { ReactElement, useContext, useMemo } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import RegistrationPage from "./containers/RegistrationPage";
import PatientSummaryPage from "./containers/PatientSummaryPage";
import ErrorPage, { ErrorTypeEnum } from "./containers/ErrorPage";
import ScreeningForm from "./components/forms/ScreeningForm";
import EConsentByGuidRoute from "./components/routes/form/EConsentByGuidRoute";
import MainConsentForm from "./components/forms/crf/MainConsentForm";
import OptionalConsentForm from "./components/forms/crf/OptionalConsentForm";
import ServicesAustraliaConsentForm from "./components/forms/crf/ServicesAustraliaConsentForm";
import TrialInformationPage from "./containers/TrialInformationPage";
import InvestigatorEConsentProvider from "./components/providers/form/InvestigatorEConsentProvider";
import RandomisationForm from "./components/forms/crf/RandomisationForm";
import PathologyTrackingForm from "./components/forms/crf/PathologyTrackingForm";
import CommentsAndIssuesForm from "./components/forms/crf/CommentsAndIssuesForm";
import TransferForm from "./components/forms/crf/TransferForm";
import StatusForm from "./components/forms/crf/StatusForm";

const formMapping: Record<string, ReactElement> = {
    "screening": <ScreeningForm />,
    "main-consent": <MainConsentForm />,
    "optional-consent": <OptionalConsentForm />,
    "services-australia-consent": <ServicesAustraliaConsentForm />,
    "randomisation": <RandomisationForm />,
    "pathology-tracking": <PathologyTrackingForm />,
    "comments-and-issues": <CommentsAndIssuesForm />,
    "transfer": <TransferForm />,
    "status": <StatusForm />
}


const App = () => {
    // useSentryUser();
    const { data: user } = useContext(AuthenticatedUserContext);
    const { routeParameters } = useContext(OnlinePatientManagementContext);
    
    const isMobile = useIsMobile();

    const {
        permissions: [
            canAccessOpms,
            canOpmsAdmin,
            canDataManage
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAccess,
            Permission.OpmsAdminister,
            Permission.OpmsDataManage
        ]
    );

    const canAdministerOpms = useMemo(() => {
        return canDataManage || canOpmsAdmin;
    }, [canDataManage, canOpmsAdmin]);

    // const formQueryOptionsContext: IFormQueryOptions = useMemo(() => ({
    //     canOpen: !permissionsLoading && canAdministerOpms,
    //     canProposeResolution: !permissionsLoading && canAccessOpms && !canAdministerOpms,
    //     canClose: !permissionsLoading && canAdministerOpms,
    //     titleOptions: {
    //         [QueryType.Query]: [
    //             "Missing Data",
    //             "Review Data Entry",
    //             "Source Data",
    //             "Medical Review Query",
    //             "Submit Form",
    //             "Other"
    //         ],
    //         [QueryType.ProtocolDeviation]: undefined
    //     }
    // }), [permissionsLoading, canAdministerOpms]);

    const menuItems: IMenuItem[] = useMemo(() => {
        if (!user) {
            return [];
        }

        let items: IMenuItem[] = [];

        items.push(
            {
                url: '/registration',
                label: 'Participants',
                external: false,
                newTab: false,
                icon: faUser
            }
        );

        return items;
    }, [canAdministerOpms, user]);
    
    // const additionalHeaderComponents = useMemo(() => {
    //     return (
    //         AppSettings.sentry.dsn && 
    //         AppSettings.sentry.enableReportABug ?
    //             [<ToggleFeedbackButton />] :
    //             undefined
    //     );
    // }, []);

    const invalidPermission = user && !permissionsLoading && !canAccessOpms;

    if (permissionsLoading && user) {
        return (
            <RouteLoading />
        );
    }

    if (invalidPermission) {
        return (
            <OnlinePatientManagementContentProvider
                menuItems={menuItems}
                footer={
                    isMobile ?
                        undefined :
                        <Typography variant="caption">
                            Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                        </Typography>
                }
            >
                <ErrorPage errorType={ErrorTypeEnum.e403} />
            </OnlinePatientManagementContentProvider>
        );
    }

    // if (!user) { 
    //     return (
    //         <Routes>
    //             <Route 
    //                 path="/" 
    //                 element={
    //                     <OnlinePatientManagementContentProvider
    //                         menuItems={menuItems}
    //                         footer={
    //                             isMobile ?
    //                                 undefined :
    //                                 <Typography variant="caption">
    //                                     Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
    //                                 </Typography>
    //                         }
    //                     >
    //                         <Outlet />
    //                     </OnlinePatientManagementContentProvider>
    //                 }
    //             >    
    //                 <Route
    //                     path="participant-information"
    //                     element={<Outlet />}
    //                 >
    //                     <Route index element={<TrialInformationPage />} />      
    //                 </Route>
    //             </Route>
    //         </Routes>
    //     )
    // }
    
    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <OnlinePatientManagementContentProvider
                        menuItems={menuItems}
                        footer={
                            isMobile ?
                                undefined :
                                <Typography variant="caption">
                                    Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                </Typography>
                        }
                    >
                        <Outlet />
                    </OnlinePatientManagementContentProvider>
                }
            >
                <Route 
                    path="registration" 
                    element={
                        <InstitutionsByCodesRoute 
                            loadBeforeMount 
                        >
                            <Outlet />
                        </InstitutionsByCodesRoute>
                    }
                >
                    <Route index element={
                        <RegistrationPage />
                    } />
                    <Route path={`:${routeParameters.institutionCode}`}>
                        <Route index element={
                            <RegistrationPage />
                        } />
                        <Route 
                            path={`:${routeParameters.patientStudyNumber}`} 
                            element={
                                <PatientDetailsByStudyNumberRoute 
                                    loadBeforeMount
                                >
                                    <Outlet />
                                </PatientDetailsByStudyNumberRoute>
                            }
                        >
                            <Route index element={   
                                <PatientSummaryPage />
                            }/>
                            <Route 
                                path={`:${routeParameters.eventDefinitionCode}/:${routeParameters.eventRepeat}/:${routeParameters.formDefinitionCode}/:${routeParameters.formRepeat}`}
                                element={
                                    <CrfFormByCodesRoute
                                        loadBeforeMount
                                        validateBeforeMount={{
                                            form: true
                                        }}
                                        createEvent
                                        createForm
                                    >
                                        <Outlet />
                                    </CrfFormByCodesRoute>
                                }
                            >
                                <Route index element={<FormMapping mapping={formMapping} />} />
                                <Route
                                    path="econsent"
                                    element={
                                        <InvestigatorEConsentProvider>
                                            <FormMapping mapping={formMapping} />
                                        </InvestigatorEConsentProvider>
                                    }
                                />
                            </Route>
                        </Route>
                    </Route>
                </Route>

                <Route path='/trial-consent'>
                    <Route path='information'>
                        <Route index element={<TrialInformationPage />} />
                        <Route path=":instCode" element={<TrialInformationPage />} />
                    </Route>
                    <Route 
                        path="econsent/:formDefinitionCode/:guid"
                        element={
                            <EConsentByGuidRoute
                                loadBeforeMount
                            >
                                <Outlet />
                            </EConsentByGuidRoute>
                        }
                    >
                        <Route index element={<FormMapping mapping={formMapping} />} />
                    </Route> 
                </Route>
                
                <Route path="error/403" element={<ErrorPage errorType={ErrorTypeEnum.e403} />} />
                <Route path="error/404" element={<ErrorPage errorType={ErrorTypeEnum.e404} />} />
                <Route path="error/405" element={<ErrorPage errorType={ErrorTypeEnum.e405} />} />
                <Route path="error/500" element={<ErrorPage errorType={ErrorTypeEnum.e500} />} />
                <Route index element={<Navigate to="/registration" replace />} />
                <Route path="*" element={<Navigate to="/registration" replace />} />
            </Route>
        </Routes>
    );
}

export default App;
